import React from 'react';

interface TeamMember {
  name: string;
  role: string;
  image: string;
}

const About: React.FC = () => {
  const teamMembers: TeamMember[] = [
    {
      name: 'Ojo Aramide',
      role: 'CEO / Founder',
      image: '/images/founder.jpeg'
    }
  ];

  return (
    <div className="w-full overflow-hidden pt-24">
      {/* About Section */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#f0faf7] to-[#f8fcfb] py-20 md:py-32 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6">
              <span className="text-emerald-700">About</span> <span className="text-orange-500">Us</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Connecting you to fresh food, fast and effortlessly.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="w-full max-w-5xl">
              <img
                src="/images/Aboutsec1.png"
                alt="About MarketMate - Fresh food delivery"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section Two - Who Are We */}
      <section className="relative w-full bg-white py-20 md:py-32 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            {/* Left Column */}
            <div className="flex flex-col">
              <p className="text-sm font-semibold uppercase tracking-[0.35em] text-emerald-700 mb-4">
                WHO ARE WE?
              </p>
              <h2 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-emerald-700 mb-8 leading-tight">
                Built for Everyday Fresh Food.
              </h2>
              <div className="flex items-center gap-3">
                <div className="w-12 h-1 bg-orange-500 rounded-full"></div>
                <svg className="w-8 h-8 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                We connect you directly to fresh produce and local food vendors around you — making it easy to order what you need, when you need it.
              </p>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                Born from the everyday challenge of finding quality, affordable food, our platform helps you skip the stress and get straight to what matters — fresh meals and ingredients.
              </p>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                We work closely with trusted farmers, vendors, and riders to ensure fast delivery, fair pricing, and a smooth experience from order to doorstep.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Three - Meet the Team */}
      <section className="relative w-full overflow-hidden">
        {/* Green Header */}
        <div className="bg-emerald-700 text-white py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-4">
              Meet the Team
            </h2>
            <p className="text-lg sm:text-xl text-white/90">
              The people building the future of fresh food.
            </p>
          </div>
        </div>

        {/* Team Content */}
        <div className="bg-white py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Team Description */}
            <div className="text-center mb-16">
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto">
                We're a team focused on solving a simple problem—making fresh food easier to get. From sourcing produce to delivery, we've built MarketMate to remove the stress and make the process smooth for everyone involved. It's all about connecting the right people and making it work, every single day.
              </p>
            </div>

            {/* Team Members Grid */}
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {teamMembers.map((member, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-shadow">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/90 via-emerald-900/50 to-transparent flex flex-col justify-end">
                    <div className="p-6">
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">
                        {member.name}
                      </h3>
                      <p className="text-sm sm:text-base text-emerald-100">
                        {member.role}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Four - Join the Team */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#e8f5f0] to-[#f0faf7] overflow-hidden">
        {/* Image Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12">
          <div className="flex justify-center">
            <div className="w-full max-w-5xl">
              <img
                src="/images/Aboutsec4.png"
                alt="Join MarketMate Team - Vendor, Customer, Rider"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* CTA Card Section */}
        <div className="flex justify-center pb-20 md:pb-32 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-2xl bg-orange-500 rounded-3xl p-8 sm:p-12 text-center shadow-2xl shadow-orange-500/30">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6">
              Join the MarketMate Team
            </h2>
            <p className="text-base sm:text-lg text-white/95 leading-relaxed mb-10">
              Help us make fresh food more accessible to everyone. If you're passionate about building, solving real problems, and creating impact, we'd love to have you on the journey.
            </p>
            <button className="inline-block bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 sm:py-4 px-8 sm:px-10 rounded-full transition-colors text-base sm:text-lg shadow-lg">
              View Open Roles <span className="ml-2">→</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
